import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { useOpeningSlug } from '@/hooks/use-opening-slug';

import { HiringGoalDetailsList } from './hiring-goal-details-list';
import { HiringGoalDetailsListSkeleton } from './hiring-goal-details-list-skeleton';
import { NoHiringGoals } from './no-hiring-goals';
import { GoalStatus } from './types';

import type { HiringGoalOverview } from 'api-clients/monolith/models/HiringGoalOverview';

interface HiringGoalsListContainerProps {
  activeTab: GoalStatus;
  hireATHiringGoalUrl: string;
  onClose: () => void;
}

function HiringGoalsListContainer({
  activeTab,
  hireATHiringGoalUrl,
  onClose,
}: HiringGoalsListContainerProps) {
  const openingSlug = useOpeningSlug();
  const [page, setPage] = useState(1);
  const [combinedHiringGoals, setCombinedHiringGoals] = useState<
    HiringGoalOverview[]
  >([]);

  useEffect(() => {
    setPage(1);
    setCombinedHiringGoals([]);
  }, [activeTab, openingSlug]);

  const { data, isFetching } = useQuery({
    queryKey: ['hiring-openings', openingSlug, activeTab, page],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsHiringGoals(
        openingSlug,
        page,
        activeTab,
      ),
    // Keep previous page's data while fetching the next page to avoid flicker
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!data) return;
    const responsePage = data.pagination?.current_page ?? page;
    // Avoid appending placeholder (previous) data while fetching next page
    if (responsePage !== page) return;
    setCombinedHiringGoals(prev => [...prev, ...data.hiring_goals]);
  }, [data, page]);

  const handleSetPage = (newPage: number) => {
    setPage(newPage);
  };

  // Initial load or tab-switch skeleton
  if (isFetching && page === 1 && combinedHiringGoals.length === 0) {
    return <HiringGoalDetailsListSkeleton />;
  }

  // After load, if still no goals and not fetching, show empty state
  if (!isFetching && combinedHiringGoals.length === 0) {
    return (
      <NoHiringGoals
        activeTab={activeTab}
        hireATHiringGoalUrl={hireATHiringGoalUrl}
      />
    );
  }

  const currentDataPage = data?.pagination?.current_page ?? 0;
  const showBottomSkeleton = isFetching && currentDataPage < page;

  return (
    <>
      <HiringGoalDetailsList
        hiringGoals={combinedHiringGoals}
        goalStatus={activeTab}
        pagination={data?.pagination}
        setPage={handleSetPage}
        onClose={onClose}
      />
      {/* Inline skeleton while fetching more pages */}
      {showBottomSkeleton && <HiringGoalDetailsListSkeleton />}
    </>
  );
}

export { HiringGoalsListContainer };
